import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { PropTypes } from 'prop-types';
import { withRouter } from 'react-router-dom';

import { TranslatedCopy } from '../intl/TranslatedCopy';
import OutageTimeframe from '../outages/OutageTimeframe';

import { Button, H2, P, Spinner } from '../components/';
import { todaysDateString } from '../util/dateWithoutTimezone';

import { createNotification, updateNotification, deleteNotification } from './actions';
import { notifications } from './reducer';
import NotificationInputGroup from './NotificationInputGroup';
import DeleteNotification from './DeleteNotification';
import { openModal } from '../components/modal/actions';

export class ManageNotification extends Component {
  constructor(props) {
    super(props);
    this.state = {
      id: null,
      newNotification: true,
      updatedNotificationDate: false,
      notificationLoaded: false,
      notificationType: 'PLANNED_GD_OUTAGE',
      display: {
        from: {
          date: todaysDateString()
        },
        to: {
          date: todaysDateString()
        }
      },
      outage: {
        from: {
          date: todaysDateString()
        },
        to: {
          date: todaysDateString()
        }
      }
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { notificationId }
      }
    } = this.props;
    if (notificationId) this.getNotificationDetails(notificationId);
  }

  // eslint-disable-next-line
  getNotificationDetails = id => {
    const { notifications } = this.props;
    const display = {};
    const outage = {};
    if (!notifications.length) {
      return this.backToDashboard();
    }
    const selectedNotification = notifications.filter(notification => notification.id === id)[0];
    display.from = this.decodeTimestamp(selectedNotification.displayStart);
    display.to = this.decodeTimestamp(selectedNotification.displayEnd);
    outage.from = this.decodeTimestamp(selectedNotification.outageStart);
    outage.to = this.decodeTimestamp(selectedNotification.outageEnd);
    this.setState({
      display,
      outage,
      notificationType: selectedNotification.notificationType,
      newNotification: false,
      id
    });
  };

  setDate = ({ name, value }) => {
    const { display, outage, newNotification } = this.state;
    const segments = name.split('_');
    const type = segments[0] === 'display' ? 'display' : 'outage';
    const toFrom = segments[1] === 'to' ? 'to' : 'from';

    if (type === 'display') {
      display[toFrom].date = value;
      if (new Date(display.to.date) < new Date(display.from.date)) {
        display.to.date = value;
      }
      return this.setState({ display, updatedNotificationDate: !newNotification });
    }
    outage[toFrom].date = value;
    if (new Date(outage.to.date) < new Date(outage.from.date)) {
      outage.to.date = value;
    }
    return this.setState({ outage, updatedNotificationDate: !newNotification });
  };

  buildPayload = e => {
    const { display, outage, notificationType } = this.state;

    display.from.hour = e.display_from_hour;
    display.from.minute = e.display_from_minute;
    display.from.timePeriod = e.display_from_timePeriod.value;

    display.to.hour = e.display_to_hour;
    display.to.minute = e.display_to_minute;
    display.to.timePeriod = e.display_to_timePeriod.value;

    outage.from.hour = e.outage_from_hour;
    outage.from.minute = e.outage_from_minute;
    outage.from.timePeriod = e.outage_from_timePeriod.value;

    outage.to.hour = e.outage_to_hour;
    outage.to.minute = e.outage_to_minute;
    outage.to.timePeriod = e.outage_to_timePeriod.value;

    return {
      displayStart: this.convertToTimestamp(display.from),
      displayEnd: this.convertToTimestamp(display.to),
      outageStart: this.convertToTimestamp(outage.from),
      outageEnd: this.convertToTimestamp(outage.to),
      notificationType
    };
  };

  offsetTimestamp = (timestamp, timeZone) => new Date(timestamp).toLocaleString('en-US', { timeZone });

  buildDate = dateSegments => {
    const month = dateSegments[0] < 10 ? `0${dateSegments[0]}` : dateSegments[0];
    const day = dateSegments[1] < 10 ? `0${dateSegments[1]}` : dateSegments[1];
    return `${dateSegments[2]}-${month}-${day}`;
  };

  decodeTimestamp = timestamp => {
    const pacificTimestamp = this.offsetTimestamp(timestamp, 'America/Los_Angeles');
    const segments = pacificTimestamp.split(/, | /);
    const dateSegments = segments[0].split('/');
    const date = this.buildDate(dateSegments);
    const time = segments[1].split(':');
    return {
      date,
      hour: time[0],
      minute: time[1],
      timePeriod: segments[2]
    };
  };

  backToDashboard = () => {
    const { history } = this.props;
    history.push('/notifications');
  };

  convertToTimestamp = ({ date, hour, minute, timePeriod }) => {
    let utcHour;
    if (timePeriod === 'AM') {
      utcHour = +hour === 12 ? '00' : hour;
    } else {
      utcHour = +hour < 12 ? +hour + 12 : hour;
    }
    utcHour = utcHour < 10 && utcHour > 0 ? `0${utcHour}` : utcHour;
    const theDate = new Date(`${date} ${utcHour}:${minute}`);
    return new Date(theDate).toISOString();
  };

  submitNotification = e => {
    const { notificationType, newNotification, id } = this.state;
    const { createNotification, updateNotification } = this.props;

    const payload = this.buildPayload(e);

    if (newNotification) {
      createNotification(payload);
    } else {
      payload.notificationType = notificationType.substr(0, notificationType.lastIndexOf('_'));
      updateNotification({ ...payload, id });
    }
    return this.backToDashboard();
  };

  handleDelete = notificationId => {
    const { deleteNotification } = this.props;
    deleteNotification({ notificationId }).then(() => this.backToDashboard());
  };

  /* eslint-disable react/destructuring-assignment */
  checkForUpdatedFields = formValues =>
    Object.keys(formValues).some(key => {
      const segments = key.split('_');
      const formValue = (formValues[key].value ? formValues[key].value : formValues[key]).toString();
      const stateValue = this.state[segments[0]][segments[1]][segments[2]];
      return formValue !== stateValue;
    });
  /* eslint-enable react/destructuring-assignment */

  formIsValid = () => {
    const { newNotification, notificationLoaded, updatedNotificationDate } = this.state;
    const { valid, submitting, notificationsForm } = this.props;
    let updated = false;
    if (!valid || submitting) {
      return false;
    }
    if (!notificationLoaded && notificationsForm && notificationsForm.values) {
      this.setState({ notificationLoaded: true });
    }
    if (!newNotification && notificationLoaded && notificationsForm && notificationsForm.values) {
      updated = this.checkForUpdatedFields(notificationsForm.values) || updatedNotificationDate;
    }
    const isValid = (updated && !newNotification) || (!updated && newNotification);
    return isValid;
  };

  blnSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

  render() {
    const { notificationType, display, outage, newNotification, notificationLoaded, id } = this.state;
    const { handleSubmit, submitting, change, notificationsForm } = this.props;
    const isValid = this.formIsValid();
    let notificationObject = null;
    if (isValid && notificationsForm && notificationsForm.values) {
      notificationObject = this.buildPayload(notificationsForm.values);
    }

    let notificationOrderError = null;
    if (notificationObject) {
      const displayStart = new Date(this.convertToTimestamp(display.from));
      const outageStart = new Date(this.convertToTimestamp(outage.from));
      const displayEnd = new Date(this.convertToTimestamp(display.to));
      const outageEnd = new Date(this.convertToTimestamp(outage.to));
      if (
        displayStart >= outageStart ||
        outageStart >= outageEnd ||
        !this.blnSameDay(displayEnd, outageEnd) ||
        (this.blnSameDay(displayEnd, outageEnd) && displayEnd < outageEnd)
      ) {
        notificationOrderError = (
          <div className='body-4 pl9-sm pt5-sm u-full-width text-color-error'>
            <TranslatedCopy id='outages.error.order' />
          </div>
        );
      }
    }

    return (
      <div className='notifications__outer'>
        <div className='notifications__manage'>
          <div className='main'>
            {notificationType && (
              <div className='set_notification_details'>
                <H2 className='pt4-sm pb4-sm pl9-sm'>
                  <TranslatedCopy id='notifications.header.display' />
                </H2>

                <NotificationInputGroup
                  from
                  name='display_from'
                  setDate={this.setDate}
                  newNotification={newNotification}
                  dateObject={display.from}
                  isStart
                  change={change}
                  notificationLoaded={notificationLoaded}
                />

                <NotificationInputGroup
                  name='display_to'
                  setDate={this.setDate}
                  newNotification={newNotification}
                  dateObject={display.to}
                  fromDateString={display.from.date}
                  change={change}
                  notificationLoaded={notificationLoaded}
                />

                <H2 className='pt7-sm pb4-sm pl9-sm'>
                  <TranslatedCopy id='notifications.header.outage' />
                </H2>

                <NotificationInputGroup
                  from
                  name='outage_from'
                  setDate={this.setDate}
                  newNotification={newNotification}
                  dateObject={outage.from}
                  isStart
                  change={change}
                  notificationLoaded={notificationLoaded}
                />

                <NotificationInputGroup
                  name='outage_to'
                  setDate={this.setDate}
                  newNotification={newNotification}
                  dateObject={outage.to}
                  fromDateString={outage.from.date}
                  change={change}
                  notificationLoaded={notificationLoaded}
                />

                <H2 className='pt7-sm pb4-sm pl9-sm'>
                  <TranslatedCopy id='notifications.header.preview' />
                </H2>

                {isValid && notificationObject && (
                  <div className='notifications__manage_preview ml5-sm ml12-md mb7-sm p5-sm pb5-sm plr6-sm'>
                    <P>
                      <TranslatedCopy id='notifications.manage.preview' />
                    </P>
                    <P>
                      <OutageTimeframe outage={notificationObject} isNAUser />
                    </P>
                    <P>
                      <OutageTimeframe outage={notificationObject} isNAUser={false} />
                    </P>
                  </div>
                )}

                {notificationOrderError && notificationOrderError}

                <div className='ml9-sm pb9-sm mt2-sm'>
                  <Button
                    extraClassName='ncss-btn-primary-dark u-full-width mr7-sm'
                    onClick={handleSubmit(this.submitNotification)}
                    disabled={!isValid || notificationOrderError}
                    data-desc='notificationsForm__button_submit'
                  >
                    {submitting ? (
                      <Spinner className='relative-container p3-sm' />
                    ) : (
                      <TranslatedCopy id='notifications.button.submit' />
                    )}
                  </Button>
                  {!newNotification && (
                    <DeleteNotification
                      handleDelete={this.handleDelete}
                      openModal={openModal}
                      notificationId={id}
                      submitting={submitting}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

ManageNotification.propTypes = {
  createNotification: PropTypes.func.isRequired,
  updateNotification: PropTypes.func.isRequired,
  deleteNotification: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  match: PropTypes.shape({ params: PropTypes.shape({ notificationId: PropTypes.string }) }),
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOf([PropTypes.number, PropTypes.string])
    })
  ),
  change: PropTypes.func.isRequired,
  notificationsForm: PropTypes.shape({
    values: PropTypes.any
  }),
  history: PropTypes.object.isRequired
};

ManageNotification.defaultProps = {
  match: {
    params: {}
  },
  notifications: [],
  notificationsForm: {}
};

const mapStateToProps = state => ({
  notifications: notifications(state),
  notificationsForm: state.form.notificationsForm
});

const connector = connect(mapStateToProps, {
  createNotification,
  updateNotification,
  deleteNotification
});

export default reduxForm({
  form: 'notificationsForm'
})(connector(withRouter(ManageNotification)));
